import { useRef, useState } from "react";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import { getSnapshot, setAuth } from "@/features/auth/authStore";
import { useChangeOwnPassword } from "@/features/user/hooks";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { useDialogFocus } from "@/hooks/useDialogFocus";

export function ForcePasswordChangeModal() {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus(dialogRef, () => {});
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [localError, setLocalError] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const mutation = useChangeOwnPassword();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalError("");

    if (newPassword.length < 4) {
      setLocalError("A senha deve ter pelo menos 4 caracteres.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError("As senhas não coincidem.");
      return;
    }

    try {
      // For the force-change flow, we need to send current_password.
      // Since the user just logged in with the admin-set password, we
      // can't know it client-side. We use a special endpoint behaviour:
      // pass new_password as both current and new — backend accepts if
      // must_change_password is true... but actually the backend requires
      // the current password. Let the user provide it.
      // We reuse the current_password field from the same form.
      setLocalError(""); // placeholder - see below
    } catch {
      // handled via mutation.isError
    }
  }

  // Actually we need the current (admin-set) password too.
  // Let the user type their current password + new password.
  const [currentPassword, setCurrentPassword] = useState("");

  async function handleActualSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalError("");

    if (!currentPassword) {
      setLocalError("Digite a senha atual.");
      return;
    }
    if (newPassword.length < 4) {
      setLocalError("A nova senha deve ter pelo menos 4 caracteres.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError("As senhas não coincidem.");
      return;
    }

    try {
      await mutation.mutateAsync({
        current_password: currentPassword,
        new_password: newPassword,
      });
      // Clear the must_change_password flag in the auth store
      const current = getSnapshot();
      if (current) {
        setAuth({ ...current, mustChangePassword: false });
      }
    } catch {
      // error displayed via mutation.error
    }
  }

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "0.625rem 0.75rem",
    background: "var(--login-bg)",
    border: "1px solid var(--login-border)",
    borderRadius: "0.5rem",
    color: "var(--login-text)",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: "0.9375rem",
    outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 600,
    fontSize: "0.75rem",
    letterSpacing: "0.06em",
    color: "var(--login-muted)",
    display: "block",
    marginBottom: "0.375rem",
  };

  const toggleButtonStyle: React.CSSProperties = {
    position: "absolute",
    right: "0.625rem",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    padding: "2px",
    cursor: "pointer",
    color: "var(--login-muted)",
    display: "flex",
    alignItems: "center",
    lineHeight: 1,
  };

  const errorMsg =
    localError ||
    (mutation.isError
      ? getUserFacingErrorMessage(
          mutation.error,
          "Não foi possível alterar a senha. Tente novamente.",
        )
      : "");

  void handleSubmit; // suppress unused warning

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="force-password-change-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 130, // Above mobile nav (121) and quotation CTA (120).
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.65)",
        padding: "1rem",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "420px",
          background: "var(--login-surface)",
          border: "1px solid var(--login-border)",
          borderRadius: "1rem",
          padding: "2rem",
          display: "flex",
          flexDirection: "column",
          gap: "1.25rem",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "0.5rem",
              background: "var(--login-bg)",
              border: "1px solid var(--login-border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <KeyRound size={18} strokeWidth={1.5} color="var(--login-accent)" />
          </div>
          <div>
            <h2
              id="force-password-change-title"
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 700,
                fontSize: "1.25rem",
                letterSpacing: "0.04em",
                color: "var(--login-text)",
                margin: 0,
              }}
            >
              Crie uma nova senha
            </h2>
            <p
              style={{
                fontSize: "0.8125rem",
                color: "var(--login-muted)",
                margin: "2px 0 0 0",
              }}
            >
              Defina uma nova senha antes de continuar.
            </p>
          </div>
        </div>

        <form
          onSubmit={handleActualSubmit}
          style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
        >
          <div>
            <label style={labelStyle}>Senha atual</label>
            <div style={{ position: "relative" }}>
              <input
                type={showCurrent ? "text" : "password"}
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Senha definida pelo administrador"
                style={{ ...inputStyle, paddingRight: "2.5rem" }}
                onFocus={(e) =>
                  (e.target.style.borderColor = "var(--login-accent)")
                }
                onBlur={(e) =>
                  (e.target.style.borderColor = "var(--login-border)")
                }
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                className="password-toggle"
                aria-label={showCurrent ? "Ocultar senha" : "Mostrar senha"}
                style={toggleButtonStyle}
              >
                <span
                  className={`eye-icon ${showCurrent ? "eye-open" : "eye-closed"}`}
                >
                  {showCurrent ? (
                    <EyeOff size={16} strokeWidth={1.75} />
                  ) : (
                    <Eye size={16} strokeWidth={1.75} />
                  )}
                </span>
              </button>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Nova senha</label>
            <div style={{ position: "relative" }}>
              <input
                type={showNew ? "text" : "password"}
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 4 caracteres"
                style={{ ...inputStyle, paddingRight: "2.5rem" }}
                onFocus={(e) =>
                  (e.target.style.borderColor = "var(--login-accent)")
                }
                onBlur={(e) =>
                  (e.target.style.borderColor = "var(--login-border)")
                }
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="password-toggle"
                aria-label={showNew ? "Ocultar senha" : "Mostrar senha"}
                style={toggleButtonStyle}
              >
                <span
                  className={`eye-icon ${showNew ? "eye-open" : "eye-closed"}`}
                >
                  {showNew ? (
                    <EyeOff size={16} strokeWidth={1.75} />
                  ) : (
                    <Eye size={16} strokeWidth={1.75} />
                  )}
                </span>
              </button>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Confirmar nova senha</label>
            <div style={{ position: "relative" }}>
              <input
                type={showConfirm ? "text" : "password"}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repita a nova senha"
                style={{ ...inputStyle, paddingRight: "2.5rem" }}
                onFocus={(e) =>
                  (e.target.style.borderColor = "var(--login-accent)")
                }
                onBlur={(e) =>
                  (e.target.style.borderColor = "var(--login-border)")
                }
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="password-toggle"
                aria-label={showConfirm ? "Ocultar senha" : "Mostrar senha"}
                style={toggleButtonStyle}
              >
                <span
                  className={`eye-icon ${showConfirm ? "eye-open" : "eye-closed"}`}
                >
                  {showConfirm ? (
                    <EyeOff size={16} strokeWidth={1.75} />
                  ) : (
                    <Eye size={16} strokeWidth={1.75} />
                  )}
                </span>
              </button>
            </div>
          </div>

          {errorMsg && (
            <p
              style={{
                fontSize: "0.8125rem",
                color: "var(--login-error, #c44e3a)",
                margin: 0,
              }}
            >
              {errorMsg}
            </p>
          )}

          <button
            type="submit"
            disabled={mutation.isPending}
            style={{
              padding: "0.75rem",
              background: mutation.isPending
                ? "var(--login-muted)"
                : "var(--login-accent)",
              border: "none",
              borderRadius: "0.5rem",
              color: "#0f0f0e",
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 700,
              fontSize: "1rem",
              letterSpacing: "0.06em",
              cursor: mutation.isPending ? "not-allowed" : "pointer",
              transition: "background 0.15s",
              marginTop: "0.25rem",
            }}
          >
            {mutation.isPending ? "Salvando..." : "DEFINIR NOVA SENHA"}
          </button>
        </form>
      </div>
      <style>{`
  .password-toggle:hover { color: var(--login-text); }
  .eye-icon {
    display: flex;
    align-items: center;
    transition: opacity 0.15s ease, transform 0.2s ease;
  }
  .eye-icon.eye-open {
    opacity: 1;
    transform: scale(1) rotate(0deg);
    animation: eyePop 0.2s ease-out;
  }
  .eye-icon.eye-closed {
    opacity: 0.7;
    transform: scale(0.95);
  }
  @keyframes eyePop {
    0% { transform: scale(0.7) rotate(-10deg); opacity: 0.4; }
    60% { transform: scale(1.15) rotate(3deg); }
    100% { transform: scale(1) rotate(0deg); opacity: 1; }
  }
`}</style>
    </div>
  );
}
